import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Select, Col, Row } from 'antd';

import { useFilterTagStore } from 'v2/store/FilterTag';
import { run } from 'validations/ruleRunner';

import SelectTree from './SelectTree';
import Tags from './Tags';
import Validations from './validations';

interface FilterOption {
  label: string;
  value: string | number;
}

type FilterSelectOptions = (FilterOption | string)[] & { isFetching?: boolean };

interface FilterItemProps {
  name: string;
  type?: 'multiple' | 'text' | 'checkbox' | 'single' | 'multiple-text' | 'customer' | 'select-tree';
  options?: FilterSelectOptions;
  action?: () => void;
  label?: string;
  value?: string;
  tagLabel?: string;
  format?: (value: string | number) => string;
  validationType?: string;
  placeholder?: string;
  helperText?: string;
}

interface FilterValue {
  label: string;
  value: string | number;
  applied?: boolean;
  nonRemovable?: boolean;
}

interface IProps {
  onChange?: () => void;
  onApply?: () => void;
  active: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}

const FilterTags: React.FC<IProps> = ({ onChange, onApply, active, disabled, children }) => {
  const { filter, addFilter, resetFilter } = useFilterTagStore();

  const [selectedFilter, setSelectedFilter] = useState<string>('none');
  const [selectedValues, setSelectedValues] = useState<string | string[] | null>(null);
  const [customerFilter, setCustomerFilter] = useState<string>('email');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [clearInput, setClearInput] = useState(false);

  useEffect(() => {
    return () => {
      resetFilter();
    };
  }, [resetFilter]);

  const getChildrenItems = useCallback((childrenNodes: React.ReactNode): FilterItemProps[] => {
    return React.Children.map(childrenNodes, child => (child as React.ReactElement<FilterItemProps>).props) ?? [];
  }, []);

  const errorFor = useCallback((field: string) => validationErrors[field] || '', [validationErrors]);

  const isValid = useCallback((field: string) => !!validationErrors[field], [validationErrors]);

  const onChangeSelectedValues = useCallback((values: string | string[], validation?: string) => {
    let newValidationErrors: Record<string, string> = {};

    if (validation) {
      const object: Record<string, string | string[]> = {};
      object[validation] = values;
      newValidationErrors = run(object, Validations);
    }

    setSelectedValues(values);
    setValidationErrors(newValidationErrors);
  }, []);

  const renderFilterInput = useCallback(() => {
    try {
      const items = getChildrenItems(children);
      const item = items.find((it: FilterItemProps) => it.name === selectedFilter);

      if (item) {
        if (item.action) {
          item.action();
        }
      }
    } catch (error) {
      toast.error('Ops! Ocorreu um erro no filtro!');
    }
  }, [children, getChildrenItems, selectedFilter]);

  const onChangeFilterType = useCallback((value: string) => {
    setSelectedFilter(value);
    setSelectedValues(null);
    setValidationErrors({});
  }, []);

  useEffect(() => {
    if (selectedFilter !== 'none') {
      renderFilterInput();
    }
  }, [selectedFilter, renderFilterInput]);

  const onChangeCustomerFilter = useCallback((value: string) => {
    setCustomerFilter(value);
    setSelectedValues(null);
    setValidationErrors({});
  }, []);

  const onAddFilter = useCallback(() => {
    try {
      const itemProps = getChildrenItems(children);
      const selected = itemProps.find((it: FilterItemProps) => it.name === selectedFilter);
      let prop = selected.tagLabel || selected.name;
      let old: FilterValue[] = [];
      let values: FilterValue | FilterValue[] = selectedValues as unknown as FilterValue | FilterValue[];

      if (selected.type === 'multiple-text') {
        values = (selectedValues as string[]).map(item => ({ label: item, value: item }));
      }

      if (selected.type === 'multiple') {
        values = (selected.options as FilterOption[])
          .filter(item => (selectedValues as string[]).includes(item.value as string))
          .map(item => ({ label: item.label, value: item.value }));
      }

      if (prop === 'Cliente') {
        const pname = customerFilter;
        prop = pname === 'email' ? 'Email do cliente' : pname === 'name' ? 'Nome do cliente' : 'CPF/CNPJ do cliente';
        const rawValue = selectedValues as string;

        if (prop === 'Nome do cliente') {
          values = { label: rawValue, value: rawValue };
        } else {
          values = [{ label: rawValue, value: rawValue }];
        }
      }

      if (filter[prop] && Array.isArray(filter[prop])) {
        old = (filter[prop] as FilterValue[]).filter(item => {
          const valuesArr = values as FilterValue[];

          for (let i = 0; i < valuesArr.length; i++) {
            if (valuesArr[i].value === item.value) {
              return false;
            }
          }

          return true;
        });
      }

      const newFilter: Record<string, FilterValue | FilterValue[]> = {};
      newFilter[prop] = Array.isArray(values) ? [...values, ...old] : values;

      addFilter(newFilter);

      setSelectedValues(null);
      setClearInput(true);
      setTimeout(() => setClearInput(false), 0);

      if (onChange) {
        onChange();
      }
    } catch (error) {
      // silent catch matching original behavior
    }
  }, [children, customerFilter, addFilter, filter, getChildrenItems, onChange, selectedFilter, selectedValues]);

  const onChangeValueEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChangeSelectedValues(e.target.value, 'email'),
    [onChangeSelectedValues]
  );

  const onChangeValueName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChangeSelectedValues(e.target.value),
    [onChangeSelectedValues]
  );

  const onChangeValueDocument = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChangeSelectedValues(e.target.value),
    [onChangeSelectedValues]
  );

  if (!active) {
    return null;
  }

  const itemProps = getChildrenItems(children);
  const selected = itemProps.find((it: FilterItemProps) => it.name === selectedFilter);
  const onChangeSelectedValue = (value: string | string[]) => onChangeSelectedValues(value, selected?.validationType);
  console.log({ selected });

  return (
    <div className='bg-grey-200 relative p-4'>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={6}>
          <Select
            className='w-full'
            value={selectedFilter}
            placeholder='Selecione um tipo de filtro'
            onChange={onChangeFilterType}
            disabled={disabled}
          >
            <Select.Option value='none' disabled>
              Selecione um tipo de filtro
            </Select.Option>
            {itemProps &&
              itemProps.map(item => (
                <Select.Option key={'filter' + item.name} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
          </Select>
        </Col>
        <Col xs={24} sm={14} md={14}>
          {selected?.type === 'multiple' && (
            <Select
              mode='multiple'
              value={selectedValues || []}
              onChange={onChangeSelectedValue}
              placeholder={`Informe um ou mais ${selected.name}`}
              disabled={selected?.options?.isFetching}
              className='w-full'
            >
              {(selected?.options as FilterOption[])?.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          )}
          {selected && selected.type === 'multiple-text' && (
            <Select
              mode='tags'
              value={selectedValues || []}
              onChange={newValue => onChangeSelectedValues(newValue)}
              placeholder={`Informe um ou mais ${selected.name}`}
              className='w-full border-0 bg-white shadow-none'
              options={(selected.options || []).map(opt => ({
                label: typeof opt === 'string' ? opt : opt.label,
                value: typeof opt === 'string' ? opt : opt.value
              }))}
            />
          )}
          {selected && selected.type === 'single' && (
            <Select
              value={selectedValues || undefined}
              className='w-full'
              placeholder={
                !selected.options || selected.options.isFetching
                  ? 'Carregando...'
                  : selected.placeholder
                    ? selected.placeholder
                    : 'Selecione...'
              }
              onChange={(value: string) => onChangeSelectedValues(value)}
            >
              {selected.options.map((op, index) => (
                <Select.Option key={`${index}-${op}`} value={typeof op === 'string' ? op : op.value}>
                  {selected.label && typeof op !== 'string'
                    ? String(op[selected.label as keyof FilterOption])
                    : String(op)}
                </Select.Option>
              ))}
            </Select>
          )}
          {selected && selected.type === 'text' && (
            <div>
              <Input
                value={selectedValues || ''}
                status={isValid(selected.validationType) ? 'error' : undefined}
                placeholder={selected.placeholder ? selected.placeholder : ''}
                className='w-full'
                onChange={e => onChangeSelectedValues(e.target.value, selected?.validationType)}
              />
              {isValid(selected.validationType) && (
                <div className='mt-1 text-xs text-red-500'>{errorFor(selected.validationType)}</div>
              )}
              {!isValid(selected.validationType) && selected.helperText && (
                <div className='mt-1 text-xs text-gray-500'>{selected.helperText}</div>
              )}
            </div>
          )}
          {selected && selected.type === 'customer' && (
            <div className='flex'>
              <Select
                placeholder='Cliente'
                style={{ width: '30%' }}
                value={customerFilter}
                onChange={onChangeCustomerFilter}
              >
                <Select.Option value='email'>Email</Select.Option>
                <Select.Option value='name'>Nome</Select.Option>
                <Select.Option value='cpfCnpj'>CPF/CNPJ</Select.Option>
              </Select>
              {customerFilter === 'email' ? (
                <div style={{ width: '70%' }}>
                  <Input
                    className='w-full'
                    value={selectedValues || ''}
                    status={isValid('email') ? 'error' : undefined}
                    placeholder='Informe o email do cliente'
                    onChange={onChangeValueEmail}
                  />
                  {isValid('email') && <div className='mt-1 text-xs text-red-500'>{errorFor('email')}</div>}
                </div>
              ) : customerFilter === 'name' ? (
                <Input
                  className='w-full'
                  style={{ width: '70%' }}
                  value={selectedValues || ''}
                  placeholder='Informe o nome do cliente'
                  onChange={onChangeValueName}
                />
              ) : customerFilter === 'cpfCnpj' ? (
                <Input
                  className='w-full'
                  style={{ width: '70%' }}
                  value={selectedValues || ''}
                  placeholder='Informe o CPF/CPNJ do cliente'
                  onChange={onChangeValueDocument}
                />
              ) : null}
            </div>
          )}
          {selected && selected.type === 'select-tree' && (
            <SelectTree
              loading={selected.options.isFetching}
              onChange={onChangeSelectedValues}
              options={selected.options}
              itemLabel={selected.label}
              itemValue={selected.value}
              clearInput={clearInput}
            />
          )}
        </Col>
        <Col xs={24} sm={10} md={4}>
          <div style={{ textAlign: 'right', lineHeight: 1 }}>
            <Button
              disabled={
                !selectedValues ||
                (selected?.validationType && isValid(selected.validationType)) ||
                (selected?.type === 'customer' && isValid('email')) ||
                disabled
              }
              block
              type='primary'
              onClick={onAddFilter}
              icon={<PlusOutlined />}
            >
              Adicionar
            </Button>
          </div>
        </Col>
        <Col span={24}>
          <Tags onChange={onChange} onApply={onApply} disabled={disabled} />
        </Col>
      </Row>
    </div>
  );
};

export default FilterTags;
